import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, Star, TrendingUp, Award, AlertCircle } from 'lucide-react';

const ProductReviewContent = ({ review }) => {
  const [expandedSections, setExpandedSections] = useState({
    fullReview: true,
    buyingGuide: false,
    performanceAnalysis: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev?.[section],
    }));
  };

  if (!review) return null;

  return (
    <div className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden">
      {/* Content Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-200 p-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-amber-900 mb-1">Content Disclaimer</h4>
            <p className="text-sm text-amber-800 leading-relaxed">
              This review contains AI-generated content based on product specifications, user feedback, and market research. 
              While we strive for accuracy, we recommend verifying product details with manufacturers. 
              This page may contain affiliate links, and we may earn a commission from purchases made through these links at no additional cost to you.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText size={28} />
          <h2 className="text-2xl font-bold">Professional Review</h2>
        </div>
        <p className="text-white/90">{review?.title}</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Summary */}
        <div className="bg-primary/5 border-l-4 border-primary p-4 rounded">
          <h3 className="font-semibold text-text-primary mb-2">Summary</h3>
          <p className="text-text-secondary leading-relaxed">{review?.summary}</p>
        </div>

        {/* Full Review */}
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('fullReview')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h3 className="font-semibold text-text-primary flex items-center gap-2">
              <FileText size={20} />
              Full Review
            </h3>
            {expandedSections?.fullReview ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          {expandedSections?.fullReview && (
            <div className="p-6 prose max-w-none">
              <div className="text-text-secondary leading-relaxed whitespace-pre-line">
                {review?.fullReview}
              </div>
            </div>
          )}
        </div>

        {/* Best For */}
        {review?.bestFor && review?.bestFor?.length > 0 && (
          <div>
            <h3 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
              <Award size={20} className="text-accent" />
              Best For
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {review?.bestFor?.map((item, index) => (
                <div key={index} className="flex items-start gap-2 bg-accent/5 p-3 rounded-lg">
                  <Star size={16} className="text-accent mt-1 flex-shrink-0" />
                  <span className="text-text-secondary text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Features */}
        {review?.keyFeatures && review?.keyFeatures?.length > 0 && (
          <div>
            <h3 className="font-semibold text-text-primary mb-3">Key Features</h3>
            <div className="space-y-3">
              {review?.keyFeatures?.map((feature, index) => (
                <div key={index} className="border-l-2 border-primary pl-4">
                  <h4 className="font-medium text-text-primary mb-1">{feature?.feature}</h4>
                  <p className="text-sm text-text-secondary">{feature?.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Analysis */}
        {review?.performanceAnalysis && (
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection('performanceAnalysis')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <h3 className="font-semibold text-text-primary flex items-center gap-2">
                <TrendingUp size={20} />
                Performance Analysis
              </h3>
              {expandedSections?.performanceAnalysis ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {expandedSections?.performanceAnalysis && (
              <div className="p-6 space-y-4">
                {Object.entries(review?.performanceAnalysis)?.map(([key, value]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary capitalize">
                        {key?.replace(/([A-Z])/g, ' $1')?.trim()}
                      </span>
                      <span className="text-sm font-bold text-primary">{value}/10</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(value / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Value Assessment */}
        {review?.valueAssessment && (
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <h3 className="font-semibold text-text-primary mb-2">Value Assessment</h3>
            <p className="text-text-secondary leading-relaxed">{review?.valueAssessment}</p>
          </div>
        )}

        {/* Buying Guide */}
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('buyingGuide')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <h3 className="font-semibold text-text-primary flex items-center gap-2">
              <FileText size={20} />
              Buying Guide
            </h3>
            {expandedSections?.buyingGuide ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          {expandedSections?.buyingGuide && (
            <div className="p-6">
              <div className="text-text-secondary leading-relaxed whitespace-pre-line">
                {review?.buyingGuide}
              </div>
            </div>
          )}
        </div>

        {/* Comparison Context */}
        {review?.comparisonContext && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-text-primary mb-2">How It Compares</h3>
            <p className="text-text-secondary leading-relaxed">{review?.comparisonContext}</p>
          </div>
        )}

        {/* Expert Verdict */}
        <div className="bg-gradient-to-r from-accent/10 to-accent/5 border-l-4 border-accent p-6 rounded">
          <h3 className="font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Award size={20} className="text-accent" />
            Expert Verdict
          </h3>
          <p className="text-text-secondary leading-relaxed text-lg">{review?.expertVerdict}</p>
        </div>
      </div>
    </div>
  );
};

export default ProductReviewContent;
